import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import {
  type IWarehouseApi,
  type UserListItem,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { ViewModel } from "../../lib/vvm";
import {
  NAVIGATION_STORE_TOKEN,
  type INavigationStore,
  Screens,
} from "../../stores/navigation";
import { getStoredUser } from "../../utils/authStorage";

function formatLastActive(value: UserListItem["lastActive"]): string {
  if (!value) {
    return "нет активности";
  }

  const day = String(value.day).padStart(2, "0");
  const month = String(value.month).padStart(2, "0");
  const hour = String(value.hour).padStart(2, "0");
  const minute = String(value.minute).padStart(2, "0");

  return `${day}.${month}.${value.year} ${hour}:${minute}`;
}

@injectable()
export class UsersManagementPageViewModel extends ViewModel {
  @observable.shallow
  users: UserListItem[] = [];

  @observable
  isLoading = false;

  @observable
  error: string | null = null;

  @observable
  processingUserId: string | null = null;

  @observable
  deleteCandidate: UserListItem | null = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();
    makeObservable(this);
  }

  @computed
  get isAdmin(): boolean {
    return getStoredUser()?.role === "admin";
  }

  @computed
  get usersForView(): UserListItem[] {
    return [...this.users].sort((left, right) => {
      if (left.status !== right.status) {
        return left.status === "pending" ? -1 : 1;
      }
      return left.name.localeCompare(right.name, "ru");
    });
  }

  @computed
  get pendingUsersCount(): number {
    return this.users.reduce(
      (acc, user) => (user.status === "pending" ? acc + 1 : acc),
      0,
    );
  }

  @computed
  get isDeleteDialogOpen(): boolean {
    return Boolean(this.deleteCandidate);
  }

  @computed
  get deleteDialogTitle(): string {
    if (!this.deleteCandidate) {
      return "Удалить пользователя";
    }

    return `Удалить пользователя "${this.deleteCandidate.name}"?`;
  }

  @computed
  get deleteDialogDescription(): string {
    if (!this.deleteCandidate) {
      return "";
    }

    return `ID: ${this.deleteCandidate.id}\nРоль: ${this.deleteCandidate.role}`;
  }

  @computed
  get pageSubtitle(): string {
    return `всего: ${this.users.length} · ожидают: ${this.pendingUsersCount}`;
  }

  protected onViewMounted(): void {
    void this.refresh();
  }

  @action.bound
  goBack(): void {
    this.navigationStore.setScreen(Screens.Main);
  }

  @action.bound
  async refresh(): Promise<void> {
    if (!this.isAdmin) {
      this.users = [];
      this.error = "Доступно только администратору";
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const users = await this.api.getUsers();
      runInAction(() => {
        this.users = users;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить пользователей";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  async approveUser(user: UserListItem): Promise<void> {
    if (this.processingUserId || user.status !== "pending") {
      return;
    }

    this.processingUserId = user.id;
    this.error = null;

    try {
      await this.api.approveUser(user.id);
      await this.refresh();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось одобрить пользователя";
      });
    } finally {
      runInAction(() => {
        this.processingUserId = null;
      });
    }
  }

  @action.bound
  requestDeleteUser(user: UserListItem): void {
    if (this.processingUserId) {
      return;
    }

    this.deleteCandidate = user;
  }

  @action.bound
  cancelDeleteUser(): void {
    this.deleteCandidate = null;
  }

  @action.bound
  async confirmDeleteUser(): Promise<void> {
    const candidate = this.deleteCandidate;
    if (!candidate || this.processingUserId) {
      return;
    }

    this.processingUserId = candidate.id;
    this.error = null;

    try {
      await this.api.deleteUser(candidate.id);
      runInAction(() => {
        this.deleteCandidate = null;
      });
      await this.refresh();
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось удалить пользователя";
      });
    } finally {
      runInAction(() => {
        this.processingUserId = null;
      });
    }
  }

  formatLastActive = formatLastActive;

  @action.bound
  isUserProcessing(userId: string): boolean {
    return this.processingUserId === userId;
  }
}
